import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Wallet, Upload, FileDown, Loader2, TrendingDown, Users, ArrowDownRight, ArrowUpRight, X, Search, Link2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line, CartesianGrid } from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { Money, SaudiRiyal } from "./SaudiRiyal";
import { useLang } from "../i18n/LanguageContext";
import { personMatch } from "../lib/searchText";

const money = (n) => <Money value={n} />;
const moneyTxt = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ⃁`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const barColor = (i, total) => {
  const shades = ["#dc2626", "#e11d48", "#ea580c", "#f97316", "#f59e0b"];
  return shades[Math.min(i, shades.length - 1)];
};

const WalletDetailDialog = ({ riderId, onClose }) => {
  const { dir } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.walletDetail(riderId).then(setData).catch(() => setData({ ops: [], trend: [] })); }, [riderId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg font-body max-h-[85vh] overflow-y-auto" dir={dir} data-testid="wallet-detail-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5 text-blue-600" />
            {data?.name || `المندوب ${riderId}`}
          </DialogTitle>
          <DialogDescription className="text-right text-xs text-slate-400">عمليات المحفظة السابقة والرصيد الحالي للمندوب</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
              <div>
                <p className="text-[11px] text-slate-500">Rider Id</p>
                <p className="font-mono-nums text-sm text-slate-700">{riderId}{data.branch ? ` · ${data.branch}` : ""}</p>
              </div>
              <div className="text-left">
                <p className="text-[11px] text-slate-500">الرصيد الحالي</p>
                <p className={`font-heading text-xl font-bold font-mono-nums ${(data.balance || 0) < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(data.balance)}</p>
              </div>
            </div>

            {data.trend && data.trend.length > 1 && (
              <div className="rounded-xl border border-slate-100 p-3">
                <p className="text-xs font-semibold text-slate-600 mb-2">تغيّر الرصيد عبر الوقت</p>
                <div style={{ width: "100%", height: 160 }}>
                  <ResponsiveContainer>
                    <LineChart data={data.trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} width={48} />
                      <Tooltip formatter={(v) => moneyTxt(v)} />
                      <Line type="monotone" dataKey="balance" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">عمليات المحفظة السابقة</p>
              {data.ops.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-400" data-testid="wallet-ops-empty">لا توجد عمليات — ارفع ملف المحافظ في تواريخ مختلفة لبناء السجل.</p>
              ) : (
                <div className="space-y-1.5" data-testid="wallet-ops-list">
                  {data.ops.map((op, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2">
                      <div className="flex items-center gap-2">
                        {op.type === "opening" ? (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold">فتح</span>
                        ) : op.change < 0 ? (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-rose-600"><ArrowDownRight className="h-3.5 w-3.5" /></span>
                        ) : op.change > 0 ? (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-600"><ArrowUpRight className="h-3.5 w-3.5" /></span>
                        ) : (
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-slate-400">=</span>
                        )}
                        <div>
                          <p className="text-xs text-slate-700">{op.type === "opening" ? "الرصيد الافتتاحي" : op.change < 0 ? "صرف / خصم" : op.change > 0 ? "إضافة / شحن" : "بدون تغيّر"}</p>
                          <p className="text-[10px] text-slate-400 font-mono-nums">{op.date}</p>
                        </div>
                      </div>
                      <div className="text-left">
                        <p className={`text-sm font-bold font-mono-nums ${op.change < 0 ? "text-rose-600" : op.change > 0 ? "text-emerald-600" : "text-slate-500"}`}>
                          {op.type === "opening" ? money(op.change) : (<span className="inline-flex items-center gap-1">{op.change > 0 ? "+" : ""}{money(op.change)}</span>)}
                        </p>
                        <p className="text-[10px] text-slate-400 font-mono-nums">الرصيد: {money(op.balance)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const WalletsPanel = ({ canEdit }) => {
  const { dir } = useLang();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [uploadDate, setUploadDate] = useState(todayStr());
  const [driveUrl, setDriveUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.walletsOverview().then(setData).catch(() => setData({ rows: [], summary: null, dates: [], top: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await api.uploadWallets(f, uploadDate);
      toast.success(`تم استيراد ${r.inserted} محفظة (${r.snapshot_date})`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الاستيراد"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const onDrive = async () => {
    if (!driveUrl.trim()) { toast.error("ألصق رابط Google Sheets"); return; }
    setUploading(true);
    try {
      const r = await api.uploadWalletsFromDrive(driveUrl.trim(), uploadDate);
      toast.success(`تم استيراد ${r.inserted} محفظة (${r.snapshot_date})`);
      setDriveUrl(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الاستيراد من الرابط"); }
    finally { setUploading(false); }
  };

  const s = data?.summary;
  const top = data?.top || [];
  const q = query.trim().toLowerCase();
  const rows = (data?.rows || []).filter((r) => personMatch(r, q));

  return (
    <div className="space-y-5" data-testid="wallets-panel" dir={dir}>
      {/* Upload bar */}
      {canEdit && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
          <p className="font-heading font-bold text-slate-800 flex items-center gap-2"><Wallet className="h-5 w-5 text-blue-600" /> استيراد المحافظ</p>
          <p className="text-[11px] text-slate-400">ارفع ملف Excel بعمودَي <span className="font-mono-nums">Rider Id</span> و <span className="font-mono-nums">Current Wallet</span>. الرفعات المتتابعة (بتواريخ مختلفة) تبني سجل عمليات كل محفظة تلقائيًا.</p>
          <div className="flex items-end gap-2 flex-wrap">
            <label className="block"><span className="text-[11px] text-slate-500">تاريخ الكشف</span>
              <Input type="date" value={uploadDate} onChange={(e) => setUploadDate(e.target.value)} className="h-9 w-40" dir="ltr" data-testid="wallets-date" /></label>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="wallets-file-input" />
            <Button size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="wallets-upload-btn" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع Excel
            </Button>
            <Button size="sm" variant="ghost" asChild className="h-9 gap-1.5 text-slate-500" data-testid="wallets-template-link">
              <a href={api.walletsTemplateUrl()}><FileDown className="h-4 w-4" /> قالب</a>
            </Button>
          </div>
          <div className="flex items-end gap-2">
            <label className="block flex-1"><span className="text-[11px] text-slate-500">أو رابط Google Sheets</span>
              <Input value={driveUrl} onChange={(e) => setDriveUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/..." className="h-9" dir="ltr" data-testid="wallets-drive-url" /></label>
            <Button size="sm" variant="outline" disabled={uploading} onClick={onDrive} data-testid="wallets-drive-btn" className="h-9 gap-1.5"><Link2 className="h-4 w-4" /> استيراد</Button>
          </div>
        </div>
      )}

      {!data ? (
        <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : rows.length === 0 && !q ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 text-center" data-testid="wallets-empty">
          <Wallet className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-slate-500 font-semibold">لا توجد محافظ بعد</p>
          <p className="text-sm text-slate-400">ارفع ملف المحافظ لعرض لوحة أعلى المناديب صرفًا.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="wallets-kpi-count">
              <p className="text-xs text-slate-500 flex items-center gap-1"><Users className="h-3.5 w-3.5" /> عدد المحافظ</p>
              <p className="font-heading text-2xl font-bold text-slate-800 font-mono-nums">{s?.count || 0}</p>
              <p className="text-[10px] text-slate-400">مرتبطة باسم: {s?.matched || 0}</p>
            </div>
            <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-4" data-testid="wallets-kpi-spend">
              <p className="text-xs text-rose-700 flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> إجمالي الصرف</p>
              <p className="font-heading text-2xl font-bold text-rose-700 font-mono-nums">{money(s?.total_spend)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="wallets-kpi-balance">
              <p className="text-xs text-slate-500">صافي الأرصدة</p>
              <p className={`font-heading text-2xl font-bold font-mono-nums ${(s?.total_balance || 0) < 0 ? "text-rose-600" : "text-emerald-600"}`}>{money(s?.total_balance)}</p>
            </div>
            <button type="button" onClick={() => s?.top && setDetail(s.top.rider_id)} data-testid="wallets-kpi-top"
              className="text-start rounded-2xl border border-rose-200 bg-rose-50/60 p-4 transition hover:shadow-md hover:border-rose-400">
              <p className="text-xs text-rose-700 flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> الأعلى صرفًا</p>
              <p className="font-heading text-base font-bold text-rose-800 truncate">{s?.top ? (s.top.name) : "—"}</p>
              <p className="text-sm font-bold text-rose-600 font-mono-nums">{s?.top ? money(s.top.spend) : ""}</p>
            </button>
          </div>

          {/* Interactive top-spenders chart */}
          {top.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="font-heading font-bold text-slate-800 mb-1">أعلى المناديب صرفًا للمحفظة</p>
              <p className="text-[11px] text-slate-400 mb-3">اضغط على أي عمود لعرض عمليات المحفظة السابقة للمندوب.</p>
              <div style={{ width: "100%", height: Math.max(220, top.length * 34) }} data-testid="wallets-top-chart">
                <ResponsiveContainer>
                  <BarChart data={top} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} interval={0} />
                    <Tooltip formatter={(v) => [moneyTxt(v), "الصرف"]} cursor={{ fill: "#fef2f2" }} />
                    <Bar dataKey="spend" radius={[0, 6, 6, 0]} cursor="pointer"
                      onClick={(d) => d && setDetail(d.rider_id)}>
                      {top.map((entry, i) => <Cell key={i} fill={barColor(i)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between gap-3 p-3 border-b border-slate-100 flex-wrap">
              <p className="font-heading font-bold text-slate-800">كل المحافظ</p>
              <div className="relative">
                <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="بحث بالاسم أو Rider Id…" className="h-9 w-56 pr-8" data-testid="wallets-search" />
              </div>
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-slate-500 text-xs border-b border-slate-100">
                    <th className="text-right font-medium px-3 py-2">المندوب</th>
                    <th className="text-right font-medium px-3 py-2">Rider Id</th>
                    <th className="text-right font-medium px-3 py-2">الفرع</th>
                    <th className="text-right font-medium px-3 py-2 w-32">الرصيد الحالي</th>
                    <th className="text-right font-medium px-3 py-2 w-24">العمليات</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={5} className="py-8 text-center text-slate-400" data-testid="wallets-no-match">لا نتائج مطابقة</td></tr>
                  ) : rows.map((r) => (
                    <tr key={r.rider_id} onClick={() => setDetail(r.rider_id)} data-testid={`wallet-row-${r.rider_id}`}
                      className="border-b border-slate-50 hover:bg-blue-50/40 cursor-pointer transition">
                      <td className="px-3 py-2.5 font-semibold text-slate-700">{r.name || <span className="text-slate-400">غير مرتبط</span>}</td>
                      <td className="px-3 py-2.5 font-mono-nums text-slate-500 text-xs">{r.rider_id}</td>
                      <td className="px-3 py-2.5 text-slate-500 text-xs">{r.branch || "—"}</td>
                      <td className={`px-3 py-2.5 text-right font-bold font-mono-nums whitespace-nowrap ${(r.balance || 0) < 0 ? "text-rose-600" : "text-emerald-600"}`} dir="ltr">{money(r.balance)}</td>
                      <td className="px-3 py-2.5 text-right text-xs text-slate-400 font-mono-nums">{r.ops_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden divide-y divide-slate-100">
              {rows.length === 0 ? (
                <p className="py-8 text-center text-slate-400 text-sm">لا نتائج مطابقة</p>
              ) : rows.map((r) => (
                <button key={r.rider_id} onClick={() => setDetail(r.rider_id)} data-testid={`wallet-card-${r.rider_id}`}
                  className="w-full text-right px-3 py-3 hover:bg-blue-50/40 transition flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm truncate">{r.name || <span className="text-slate-400">غير مرتبط</span>}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                      <span className="font-mono-nums">{r.rider_id}</span>
                      {r.branch ? <span> • {r.branch}</span> : null}
                      <span> • {r.ops_count} عملية</span>
                    </p>
                  </div>
                  <span className={`shrink-0 font-bold font-mono-nums text-sm ${(r.balance || 0) < 0 ? "text-rose-600" : "text-emerald-600"}`} dir="ltr">{money(r.balance)}</span>
                </button>
              ))}
            </div>
            {s?.last_date && <p className="text-[10px] text-slate-400 px-3 py-2">آخر تحديث: {s.last_date}</p>}
          </div>
        </>
      )}

      {detail && <WalletDetailDialog riderId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};
